import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { explorerEventPush, fileListQuery, wsChannel } from '@xdocs/schemas';
import type { BreadcrumbEntry, ErrorAlert, ExplorerEntry, FileListResult } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'name', dir: 'asc' };

export const EXPLORER_LIST_QUERY_KEY_PREFIX = 'explorer-list' as const;

export interface UseExplorerListResult {
  rows: readonly ExplorerEntry[];
  total: number | null;
  nextCursor: string | null;
  breadcrumbs: readonly BreadcrumbEntry[];
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
}

/**
 * Server-side pagination/sort/search over `file.list` (F7, E4 §8.3),
 * folder-scoped. Mirrors `routes/documents/useDocumentsList.ts`'s
 * desktop-numbered / mobile-cursor-append bridge over the same
 * offset-paginated op, plus a `folderId` axis (resets to page 1 on
 * navigation) and the breadcrumb chain the response carries.
 */
export function useExplorerList(folderId: string | null): UseExplorerListResult {
  const { wsClient, connectionState } = useWs();
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<ExplorerEntry[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';

  // Folder navigation always resets to a fresh page-1 replace fetch — never
  // append across a folder boundary. Only re-run on `folderId` change;
  // resetting `query` itself must not re-trigger this (intentionally
  // dep-limited — `react-hooks/exhaustive-deps` isn't registered for plain
  // `.ts` files under this repo's ESLint config, `.jsx`/`.tsx` only, so no
  // disable directive is valid/needed here).
  useEffect(() => {
    appendModeRef.current = false;
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, [folderId]);

  const queryKey = [EXPLORER_LIST_QUERY_KEY_PREFIX, folderId, query] as const;

  const result = useQuery<FileListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'file.list', fileListQuery.parse({ ...query, folderId })),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  useEffect(() => {
    if (!userId || connectionState !== 'connected') return undefined;
    const channel = wsChannel.user(userId);
    return wsClient.subscribe(channel, (envelope) => {
      const parsed = explorerEventPush.safeParse(envelope.payload);
      if (!parsed.success) return; // not an explorer event (e.g. credit/notification push on the same channel)
      void queryClient.invalidateQueries({ queryKey: [EXPLORER_LIST_QUERY_KEY_PREFIX] });
    });
  }, [wsClient, queryClient, userId, connectionState]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    breadcrumbs: result.data?.breadcrumbs ?? [],
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
  };
}
